import React from "react";
import ReactDOM from "react-dom/client";
import { Analytics } from "@vercel/analytics/react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";
import { AuthProvider } from "./providers/AuthProvider";
import { useAuth } from "@/hooks/auth/useAuth";

import "./styles/tailwind.css";
import "./styles/globals.css";

// Layouts
import PublicLayout from "@/public/layout/PublicLayout";
import AdminLayout from "@/admin/layout/AdminLayout";

const lazyPage = <T extends React.ComponentType>(
  loader: () => Promise<{ default: T }>
) => React.lazy(loader);

const AdminDashboardPage = lazyPage(() => import("@/admin/pages/AdminDashboardPage"));
const AdminToursPage = lazyPage(() => import("@/admin/pages/AdminToursPage"));
const AdminTourBuilderPage = lazyPage(() => import("@/admin/pages/AdminTourBuilderPage"));
const AdminSeasonBuilderPage = lazyPage(() => import("@/admin/pages/AdminSeasonBuilderPage"));
const AdminEventBuilderPage = lazyPage(() => import("@/admin/pages/AdminEventBuilderPage"));
const AdminMatchBuilderPage = lazyPage(() => import("@/admin/pages/AdminMatchBuilderPage"));
const AdminMembersPage = lazyPage(() => import("@/admin/pages/AdminMembersPage"));
const OwnerWorkspacePage = lazyPage(() => import("@/admin/pages/OwnerWorkspacePage"));
const TourSettingsPage = lazyPage(() => import("@/admin/pages/TourSettingsPage"));
const ScheduleWorkspacePage = lazyPage(() => import("@/admin/pages/ScheduleWorkspacePage"));
const TourSetupPage = lazyPage(() => import("@/admin/pages/TourSetupPage"));
const HomePage = lazyPage(() => import("@/public/pages/HomePage"));
const SchedulePage = lazyPage(() => import("@/public/pages/SchedulePage"));
const StandingsPagePage = lazyPage(() => import("@/public/pages/StandingsPage"));
const MorePage = lazyPage(() => import("@/public/pages/MorePage"));
const NewsPage = lazyPage(() => import("@/public/pages/NewsPage"));
const ArticleDetailPage = lazyPage(() => import("@/public/pages/ArticleDetailPage"));
const SubmitArticlePage = lazyPage(() => import("@/public/pages/SubmitArticlePage"));
const PatchNotesPage = lazyPage(() => import("@/public/pages/PatchNotesPage"));
const AboutPage = lazyPage(() => import("@/public/pages/AboutPage"));
const RulesPage = lazyPage(() => import("@/public/pages/RulesPage"));
const AchievementsPage = lazyPage(() => import("@/public/pages/AchievementsPage"));
const EventDetailPage = lazyPage(() => import("@/public/pages/EventDetailPage"));
const PlayerProfilePage = lazyPage(() => import("@/public/pages/PlayerProfilePage"));
const PlayerAchievementsPage = lazyPage(() => import("@/public/pages/PlayerAchievementsPage"));
const TeamBalancerPage = lazyPage(() => import("@/public/pages/TeamBalancerPage"));
const StatsPage = lazyPage(() => import("@/public/pages/StatsPage"));
const CourseStatsPage = lazyPage(() => import("@/public/pages/CourseStatsPage"));
const UserSettingsPage = lazyPage(() => import("@/player/pages/UserSettingsPage"));
const PlayerSettingsPage = lazyPage(() => import("@/player/pages/PlayerSettingsPage"));
const PlayHomePage = lazyPage(() => import("@/play/pages/PlayHomePage"));
const RoundPage = lazyPage(() => import("@/play/pages/RoundPage"));
const CreateTourPage = lazyPage(() => import("@/public/pages/CreateTourPage"));
const AcceptInvitePage = lazyPage(() => import("@/public/pages/AcceptInvitePage"));
const DiscoverToursPage = lazyPage(() => import("@/public/pages/DiscoverToursPage"));
const GuestAccessPage = lazyPage(() => import("@/public/pages/GuestAccessPage"));

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function ThemeSync() {
  const { user } = useAuth();

  React.useEffect(() => {
    const theme = user?.userSettings?.theme ?? "classicCompact";
    document.documentElement.dataset.theme = theme;
  }, [user?.userSettings?.theme]);

  return null;
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <Analytics />
        <ThemeSync />
        <ScrollToTop />
        <React.Suspense fallback={<div className="min-h-screen bg-[#f5f1e6]" />}>
          <Routes>
          {/* ================= PUBLIC ================= */}
          <Route path="/create-tour" element={<CreateTourPage />} />
          <Route path="/join/:token" element={<AcceptInvitePage />} />
          <Route path="/guest" element={<GuestAccessPage />} />
          <Route path="/guest/:code" element={<GuestAccessPage />} />
          <Route path="/discover" element={<DiscoverToursPage />} />
          <Route path="/:tourId?" element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="events/:eventId" element={<EventDetailPage />} />
            <Route path="players/:playerId" element={<PlayerProfilePage />} />
            <Route
              path="players/:playerId/achievements"
              element={<PlayerAchievementsPage />}
            />
            <Route path="standings" element={<StandingsPagePage />} />
            <Route path="more" element={<MorePage />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="news/submit" element={<SubmitArticlePage />} />
            <Route path="news/:articleId" element={<ArticleDetailPage />} />
            <Route path="patch-notes" element={<PatchNotesPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="achievements" element={<AchievementsPage />} />
            <Route path="stats" element={<StatsPage />} />
            <Route
              path="stats/courses/:courseId"
              element={<CourseStatsPage />}
            />
            <Route path="user-settings" element={<UserSettingsPage />} />
            <Route path="player-settings" element={<PlayerSettingsPage />} />
            <Route path="tools/team-balancer" element={<TeamBalancerPage />} />
            <Route path="play" element={<PlayHomePage />} />
            <Route path="play/match/:matchId" element={<RoundPage />} />
          </Route>

          {/* ================= ADMIN ================= */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="tours" element={<AdminToursPage />} />
            <Route path="tours/:tourId" element={<AdminTourBuilderPage />} />
            <Route
              path="tours/:tourId/workspace"
              element={<OwnerWorkspacePage />}
            />
            <Route
              path="tours/:tourId/setup/:mode"
              element={<TourSetupPage />}
            />
            <Route
              path="tours/:tourId/setup/:mode/:stepId"
              element={<TourSetupPage />}
            />
            <Route
              path="tours/:tourId/settings"
              element={<TourSettingsPage />}
            />
            <Route
              path="tours/:tourId/schedule"
              element={<ScheduleWorkspacePage />}
            />
            <Route
              path="tours/:tourId/members"
              element={<AdminMembersPage />}
            />
            <Route
              path="tours/:tourId/seasons/:seasonId"
              element={<AdminSeasonBuilderPage />}
            />
            <Route
              path="tours/:tourId/seasons/:seasonId/events/:eventId"
              element={<AdminEventBuilderPage />}
            />
            <Route
              path="tours/:tourId/seasons/:seasonId/events/:eventId/matches/:matchId"
              element={<AdminMatchBuilderPage />}
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </AuthProvider>
  </React.StrictMode>
);
